(() => {
  'use strict';

  const USA_CENTER = { lat: 39.5, lng: -98.35 };
  const EXPANSION_RADII = [25, 50, 100, 250, 500];

  function escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function normalizeRetailerName(value = '') {
    return String(value)
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[’']/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function retailerKey(store) {
    if (store?.source === 'target') return 'target';

    const normalized = normalizeRetailerName(store?.chain || store?.name || '');

    if (normalized.includes('shoprite') || normalized.includes('shop rite')) return 'shoprite';
    if (normalized.includes('save mart') || normalized.includes('savemart')) return 'savemart';
    if (normalized === 'lucky' || normalized.includes('lucky supermarket')) return 'lucky';
    if (normalized.includes('fred meyer')) return 'fredmeyer';
    if (normalized === 'kroger' || normalized.includes('kroger')) return 'kroger';
    if (normalized === 'qfc' || normalized.includes('quality food center')) return 'qfc';
    if (normalized.includes('raleys')) return 'raleys';
    if (normalized.includes('dierberg')) return 'dierbergs';

    return 'fallback';
  }

  function toLatLngLiteral(value) {
    if (!value) return null;
    if (typeof value.lat === 'function' && typeof value.lng === 'function') {
      return { lat: value.lat(), lng: value.lng() };
    }
    if (Number.isFinite(Number(value.lat)) && Number.isFinite(Number(value.lng))) {
      return { lat: Number(value.lat), lng: Number(value.lng) };
    }
    return null;
  }

  function haversineMiles(a, b) {
    const R = 3958.7613;
    const rad = (deg) => (deg * Math.PI) / 180;
    const dLat = rad(b.lat - a.lat);
    const dLng = rad(b.lng - a.lng);
    const lat1 = rad(a.lat);
    const lat2 = rad(b.lat);
    const sinLat = Math.sin(dLat / 2);
    const sinLng = Math.sin(dLng / 2);
    const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  function mapsDirectionsUrl(address) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  }

  function fullAddress(store) {
    return [store.address, store.city, store.state, store.zip].filter(Boolean).join(', ');
  }

  function loadGoogleMaps(apiKey, mapId) {
    if (window.google?.maps?.importLibrary) return Promise.resolve();
    if (window.__punkBunnyGoogleMapsPromise) return window.__punkBunnyGoogleMapsPromise;

    window.__punkBunnyGoogleMapsPromise = new Promise((resolve, reject) => {
      if (!apiKey) {
        reject(new Error('Missing Google Maps API key.'));
        return;
      }

      const callbackName = '__punkBunnyGoogleMapsLoaded';
      window[callbackName] = () => {
        delete window[callbackName];
        resolve();
      };

      const params = new URLSearchParams({
        key: apiKey,
        loading: 'async',
        callback: callbackName,
        v: 'weekly',
        region: 'US',
        language: 'en'
      });
      if (mapId) params.set('map_ids', mapId);

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.onerror = () => reject(new Error('Google Maps failed to load.'));
      document.head.appendChild(script);
    });

    return window.__punkBunnyGoogleMapsPromise;
  }

  class PunkBunnyStoreLocator {
    constructor(root) {
      this.root = root;
      this.apiKey = root.dataset.apiKey || '';
      this.mapId = root.dataset.mapId || 'DEMO_MAP_ID';
      this.defaultRadius = Number(root.dataset.defaultRadius || 25);
      this.maxResults = Number(root.dataset.maxResults || 16);

      this.staticStores = Array.isArray(window.PUNK_BUNNY_STORE_LOCATIONS)
        ? window.PUNK_BUNNY_STORE_LOCATIONS.filter((store) => Number.isFinite(Number(store.lat)) && Number.isFinite(Number(store.lng)))
        : [];

      this.targetStores = Array.isArray(window.PUNK_BUNNY_TARGET_LOCATIONS)
        ? window.PUNK_BUNNY_TARGET_LOCATIONS.filter((store) => Number.isFinite(Number(store.lat)) && Number.isFinite(Number(store.lng)))
        : [];

      this.logoUrls = {
        target: root.dataset.logoTarget || '',
        shoprite: root.dataset.logoShoprite || '',
        savemart: root.dataset.logoSavemart || '',
        lucky: root.dataset.logoLucky || '',
        kroger: root.dataset.logoKroger || '',
        fredmeyer: root.dataset.logoFredmeyer || '',
        qfc: root.dataset.logoQfc || '',
        raleys: root.dataset.logoRaleys || '',
        dierbergs: root.dataset.logoDierbergs || ''
      };
      this.directionIcon = root.dataset.directionIcon || '';
      this.userIcon = root.dataset.userIcon || '';
      this.autoLocate = root.dataset.autoLocate !== 'false';

      this.mapEl = root.querySelector('[data-map]');
      this.resultsEl = root.querySelector('[data-results]');
      this.statusEl = root.querySelector('[data-status]');
      this.headingEl = root.querySelector('[data-results-heading]');
      this.countEl = root.querySelector('[data-result-count]');
      this.coverageEl = root.querySelector('[data-coverage]');
      this.autocompleteHost = root.querySelector('[data-autocomplete-host]');
      this.locationButton = root.querySelector('[data-use-location]');
      this.filterButtons = [...root.querySelectorAll('[data-filter]')];

      this.activeFilter = 'all';
      this.lastResults = [];
      this.markers = [];
      this.userMarker = null;
      this.infoWindow = null;
      this.searchCenter = null;

      this.updateCoverage();
    }

    updateCoverage() {
      if (!this.coverageEl) return;
      const retailerCount = this.staticStores.length.toLocaleString('en-US');
      const targetCount = this.targetStores.length.toLocaleString('en-US');
      this.coverageEl.textContent = `${retailerCount} participating retailer locations + ${targetCount} participating Target stores`;
    }

    async init() {
      if (!this.apiKey) {
        this.setStatus('Add the Google Maps browser API key in the Theme Editor to activate search and the map.');
        return;
      }

      try {
        await loadGoogleMaps(this.apiKey, this.mapId);
        const [{ Map, InfoWindow }, markerLibrary, placesLibrary] = await Promise.all([
          google.maps.importLibrary('maps'),
          google.maps.importLibrary('marker'),
          google.maps.importLibrary('places')
        ]);

        this.AdvancedMarkerElement = markerLibrary.AdvancedMarkerElement;
        this.PlaceAutocompleteElement = placesLibrary.PlaceAutocompleteElement;

        this.map = new Map(this.mapEl, {
          center: USA_CENTER,
          zoom: 4,
          mapId: this.mapId,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          gestureHandling: 'cooperative'
        });
        this.infoWindow = new InfoWindow();

        this.initAutocomplete();
        this.bindControls();

        if (!this.staticStores.length && !this.targetStores.length) {
          this.setStatus('The store data files still need their one-time geocoding step before locations can appear.');
        } else if (!this.targetStores.length) {
          this.setStatus('Retailer locations are ready. The participating Target rollout file still needs its one-time geocoding step.');
        }

        if (this.autoLocate) {
          window.setTimeout(() => this.useMyLocation(), 250);
        }
      } catch (error) {
        console.error('[Punk Bunny Store Locator]', error);
        this.setStatus('The store locator could not load. Check the Google Maps API key, Map ID, enabled APIs, and domain restrictions.');
      }
    }

    initAutocomplete() {
      const fallback = this.autocompleteHost.querySelector('.pb-locator__fallback-input');
      if (fallback) fallback.remove();

      const autocomplete = new this.PlaceAutocompleteElement({});
      autocomplete.placeholder = 'Enter city, ZIP code, or address';
      this.autocompleteHost.appendChild(autocomplete);

      autocomplete.addEventListener('gmp-select', async ({ placePrediction }) => {
        try {
          const place = placePrediction.toPlace();
          await place.fetchFields({ fields: ['location', 'formattedAddress', 'viewport'] });
          const center = toLatLngLiteral(place.location);
          if (!center) throw new Error('Selected place has no location.');
          const label = place.formattedAddress || 'your search';
          await this.searchNear(center, label);
        } catch (error) {
          console.error('[Punk Bunny Store Locator] autocomplete', error);
          this.setStatus('We could not use that search result. Try another city, ZIP code, or address.');
        }
      });
    }

    bindControls() {
      this.locationButton.addEventListener('click', () => this.useMyLocation());
      this.filterButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.activeFilter = button.dataset.filter || 'all';
          this.filterButtons.forEach((btn) => {
            const active = btn === button;
            btn.classList.toggle('is-active', active);
            btn.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          this.renderResults();
        });
      });
    }

    useMyLocation() {
      if (!navigator.geolocation) {
        this.setStatus('Your browser does not support location lookup. Search by city, ZIP code, or address instead.');
        return;
      }

      this.locationButton.disabled = true;
      this.setStatus('Getting your location…');

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          this.locationButton.disabled = false;
          await this.searchNear(
            { lat: position.coords.latitude, lng: position.coords.longitude },
            'your current location'
          );
        },
        (error) => {
          this.locationButton.disabled = false;
          console.warn('[Punk Bunny Store Locator] geolocation', error);
          this.setStatus('Location access was unavailable. Search by city, ZIP code, or address instead.');
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }

    async searchNear(center, label) {
      this.searchCenter = center;
      this.headingEl.textContent = `Stores near ${label}`;
      this.setStatus('Finding nearby Punk Bunny locations…');
      this.clearMarkers();
      this.addUserMarker(center);

      let radius = this.defaultRadius;
      let staticResults = [];
      let targetResults = [];
      const radii = [this.defaultRadius, ...EXPANSION_RADII.filter((value) => value > this.defaultRadius)];

      for (const candidateRadius of radii) {
        radius = candidateRadius;
        staticResults = this.findStaticStores(center, radius);
        targetResults = this.findTargetStores(center, radius);
        if (staticResults.length + targetResults.length >= Math.min(5, this.maxResults) || radius === EXPANSION_RADII.at(-1)) break;
      }

      const merged = [...staticResults, ...targetResults]
        .sort((a, b) => a.distance - b.distance);

      this.lastResults = merged;
      this.renderResults();

      if (merged.length) {
        this.setStatus(`Showing the closest participating locations found within ${radius} miles.`);
      } else {
        this.setStatus('No participating locations were found in this area. Try a nearby city or a broader search.');
      }
    }

    findStaticStores(center, radius) {
      return this.staticStores
        .map((store) => {
          const position = { lat: Number(store.lat), lng: Number(store.lng) };
          return {
            ...store,
            source: 'retailer',
            position,
            distance: haversineMiles(center, position),
            directionsUrl: mapsDirectionsUrl(fullAddress(store))
          };
        })
        .filter((store) => store.distance <= radius)
        .sort((a, b) => a.distance - b.distance)
        .slice(0, this.maxResults);
    }

    findTargetStores(center, radius) {
      return this.targetStores
        .map((store) => {
          const position = { lat: Number(store.lat), lng: Number(store.lng) };
          return {
            ...store,
            source: 'target',
            chain: 'Target',
            name: store.name || 'Target',
            position,
            distance: haversineMiles(center, position),
            directionsUrl: mapsDirectionsUrl(fullAddress(store))
          };
        })
        .filter((store) => store.distance <= radius)
        .sort((a, b) => a.distance - b.distance)
        .slice(0, this.maxResults);
    }

    filteredResults() {
      if (this.activeFilter === 'all') return this.lastResults;
      return this.lastResults.filter((store) => store.source === this.activeFilter);
    }

    renderResults() {
      const stores = this.filteredResults().slice(0, this.maxResults);
      this.resultsEl.innerHTML = '';
      this.resultsEl.scrollTop = 0;
      this.countEl.hidden = false;
      this.countEl.textContent = String(stores.length);

      this.clearStoreMarkers();

      if (!stores.length) {
        const empty = document.createElement('p');
        empty.className = 'pb-locator__status';
        empty.textContent = this.lastResults.length
          ? 'No locations in this filter. Try All.'
          : 'Search an address or use your location to see nearby stores.';
        this.resultsEl.appendChild(empty);
        return;
      }

      const bounds = new google.maps.LatLngBounds();
      if (this.searchCenter) bounds.extend(this.searchCenter);

      stores.forEach((store) => {
        this.resultsEl.appendChild(this.buildStoreCard(store));
        this.addStoreMarker(store);
        if (store.position) bounds.extend(store.position);
      });

      if (!bounds.isEmpty()) this.map.fitBounds(bounds, 60);
    }

    buildStoreCard(store) {
      const card = document.createElement('article');
      card.className = `pb-locator__store pb-locator__store--${store.source}`;

      const address = fullAddress(store);
      const key = retailerKey(store);
      const logoUrl = this.logoUrls[key] || '';
      const sourceLabel = store.source === 'target' ? 'Target' : escapeHtml(store.chain || 'Retailer');

      const sourceIcon = logoUrl
        ? `<img class="pb-locator__store-source-logo" src="${escapeHtml(logoUrl)}" alt="" aria-hidden="true">`
        : `<span class="pb-locator__store-source-dot" aria-hidden="true"></span>`;

      const directionIcon = this.directionIcon
        ? `<img class="pb-locator__directions-icon" src="${escapeHtml(this.directionIcon)}" alt="" aria-hidden="true">`
        : `<span class="pb-locator__directions-fallback" aria-hidden="true">↗</span>`;

      card.innerHTML = `
        <div class="pb-locator__store-top">
          <div>
            <div class="pb-locator__store-source">
              ${sourceIcon}
              <span>${sourceLabel}</span>
            </div>
            <h4 class="pb-locator__store-name">${escapeHtml(store.name || store.chain || 'Retailer')}</h4>
          </div>
          <span class="pb-locator__distance">${store.distance.toFixed(store.distance < 10 ? 1 : 0)} mi</span>
        </div>
        <p class="pb-locator__address">${escapeHtml(address)}</p>
        <a class="pb-locator__directions" href="${escapeHtml(store.directionsUrl)}" target="_blank" rel="noopener noreferrer">
          <span>GET DIRECTIONS</span>
          ${directionIcon}
        </a>
      `;
      return card;
    }

    makeMarkerContent(store) {
      const key = retailerKey(store);
      const logoUrl = this.logoUrls[key];
      const marker = document.createElement('div');
      marker.className = `pb-locator__marker pb-locator__marker--${store.source} pb-locator__marker--${key}`;

      if (logoUrl) {
        const img = document.createElement('img');
        img.className = 'pb-locator__marker-logo';
        img.src = logoUrl;
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');
        marker.appendChild(img);
      } else {
        const fallback = document.createElement('span');
        fallback.className = 'pb-locator__marker-fallback';
        fallback.textContent = store.source === 'target' ? 'T' : 'PB';
        marker.appendChild(fallback);
      }

      return marker;
    }

    addStoreMarker(store) {
      const marker = new this.AdvancedMarkerElement({
        map: this.map,
        position: store.position,
        title: store.name || store.chain || 'Punk Bunny retailer',
        gmpClickable: true
      });
      marker.append(this.makeMarkerContent(store));

      marker.addEventListener('gmp-click', () => {
        const address = fullAddress(store);
        this.infoWindow.setContent(`
          <div style="max-width:240px;color:#111">
            <strong>${escapeHtml(store.name || store.chain || 'Retailer')}</strong><br>
            <span>${escapeHtml(address)}</span><br>
            <a href="${escapeHtml(store.directionsUrl)}" target="_blank" rel="noopener noreferrer">Get directions</a>
          </div>
        `);
        this.infoWindow.open({ map: this.map, anchor: marker });
      });

      this.markers.push(marker);
    }

    addUserMarker(position) {
      if (this.userMarker) this.userMarker.map = null;

      const content = document.createElement('div');
      content.className = 'pb-locator__marker pb-locator__marker--user';

      if (this.userIcon) {
        const img = document.createElement('img');
        img.className = 'pb-locator__marker-user-icon';
        img.src = this.userIcon;
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');
        content.appendChild(img);
      } else {
        const fallback = document.createElement('span');
        fallback.className = 'pb-locator__marker-user-fallback';
        fallback.textContent = 'YOU';
        content.appendChild(fallback);
      }

      this.userMarker = new this.AdvancedMarkerElement({
        map: this.map,
        position,
        title: 'Your current location'
      });
      this.userMarker.append(content);
    }

    clearStoreMarkers() {
      this.markers.forEach((marker) => { marker.map = null; });
      this.markers = [];
    }

    clearMarkers() {
      this.clearStoreMarkers();
      if (this.userMarker) {
        this.userMarker.map = null;
        this.userMarker = null;
      }
    }

    setStatus(message) {
      this.statusEl.textContent = message;
    }
  }

  function initAll() {
    document.querySelectorAll('[data-pb-store-locator]').forEach((root) => {
      if (root.dataset.pbLocatorInitialized === 'true') return;
      root.dataset.pbLocatorInitialized = 'true';
      new PunkBunnyStoreLocator(root).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
